import { styled } from "@mui/material/styles";
import { Button, ChevronDownIcon, ChevronLeftIcon, ChevronUpIcon, focusRing, IconButton, QuestionIcon, Tag, thinScrollbar } from "@UI";
import React, { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { answerText, emptyDraft, isAnswered, isOverridden, questionsOf, selectionOf, toAnswer, toggledOptionIds, unansweredCount } from "../utils/choiceModel";
import { HookCard } from "./HookCard";
import { choiceTitle } from "./QuestionLine";
import { QuestionSet } from "./QuestionSet";
const Card = styled(HookCard)({
    border: 0,
    borderRadius: 0,
    borderBottom: "1px solid var(--j-line)",
    maxHeight: "55vh",
    "& .hook-head, & .hook-actions": {
        flexShrink: 0
    },
    "& .hook-head > svg": {
        marginTop: 1,
        color: "var(--j-ink-2)"
    },
    "& .hook-head > button": {
        margin: "calc(-1 * var(--j-sp-1-5)) calc(-1 * var(--j-sp-1-5)) 0 0"
    },
    "& .hook-actions > .lead": {
        marginRight: "auto"
    }
});
const Body = styled("div")({
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    overscrollBehavior: "contain",
    display: "flex",
    flexDirection: "column",
    gap: "var(--j-sp-3)",
    margin: "0 calc(-1 * var(--j-sp-3))",
    padding: "0 var(--j-sp-3)",
    borderBlock: "1px solid transparent",
    ...thinScrollbar,
    "&[data-overflow=true]": {
        borderColor: "var(--j-line-soft)",
        paddingBlock: "var(--j-sp-2)"
    },
    "&[data-dense=true]": {
        overflow: "hidden"
    }
});
const CollapsedLine = styled("button")({
    ...focusRing,
    "&:focus-visible": {
        outline: "var(--j-focus-w) solid var(--j-accent)",
        outlineOffset: "var(--j-focus-inset-offset)"
    },
    display: "flex",
    alignItems: "center",
    gap: "var(--j-sp-2)",
    width: "100%",
    height: "var(--j-bar)",
    padding: "0 var(--j-sp-1-5) 0 var(--j-sp-3)",
    border: 0,
    borderBottom: "1px solid var(--j-line)",
    background: "var(--j-surface)",
    fontSize: "var(--j-fs-md)",
    color: "var(--j-ink)",
    textAlign: "left",
    cursor: "pointer",
    "&:hover": {
        background: "var(--j-hover)"
    },
    "& > svg": {
        color: "var(--j-ink-2)"
    },
    "& > b": {
        fontWeight: 600,
        whiteSpace: "nowrap"
    },
    "& > .dock-title": {
        flex: 1,
        minWidth: 0,
        color: "var(--j-ink-2)",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
    },
    "& > .dock-chevron": {
        width: "var(--j-btn-h)",
        height: "var(--j-btn-h)",
        display: "grid",
        placeItems: "center",
        flexShrink: 0
    }
});
const Progress = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "var(--j-sp-1)",
    flexShrink: 0,
    "& .bars": {
        display: "flex",
        gap: "var(--j-sp-0-5)"
    },
    "& .bars > button": {
        ...focusRing,
        flex: 1,
        height: 12,
        padding: 0,
        border: 0,
        background: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        borderRadius: "var(--j-r-xs)",
        "&::before": {
            content: '""',
            flex: 1,
            height: 4,
            borderRadius: "var(--j-r-xs)",
            background: "var(--j-line-soft)",
            transition: "background-color var(--j-dur-fade) var(--j-ease)"
        },
        "&[data-state=done]::before": {
            background: "var(--j-accent)"
        },
        "&[data-state=current]::before": {
            background: "var(--j-ink-2)"
        },
        "&:disabled": {
            cursor: "default"
        }
    },
    "& .count": {
        display: "flex",
        justifyContent: "space-between",
        gap: "var(--j-sp-2)",
        fontSize: "var(--j-fs-xs)",
        lineHeight: "var(--j-lh-xs)",
        color: "var(--j-ink-3)",
        fontVariantNumeric: "tabular-nums"
    }
});
const Review = styled("div")({
    display: "flex",
    flexDirection: "column",
    "& > .review-title": {
        margin: "0 0 var(--j-sp-1)",
        fontWeight: 500,
        outline: "none"
    },
    "& > .review-row": {
        display: "flex",
        alignItems: "flex-start",
        gap: "var(--j-sp-2)",
        padding: "var(--j-sp-2) 0",
        borderTop: "1px solid var(--j-line-soft)"
    },
    "& > .review-title + .review-row": {
        borderTop: 0
    },
    "& .review-body": {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "var(--j-sp-0-5)",
        overflowWrap: "anywhere"
    },
    "& .review-body > span:nth-of-type(2)": {
        fontSize: "var(--j-fs-sm)",
        lineHeight: "var(--j-lh-sm)",
        color: "var(--j-ink-2)"
    },
    "& .review-body > span:last-of-type": {
        maxWidth: "100%"
    }
});
const DENSE_OPTION_COUNT = 7;
function useOverflow(ref, isEnabled) {
    const [hasOverflow, setHasOverflow] = useState(false);
    useLayoutEffect(()=>{
        const element = ref.current;
        if (!element || !isEnabled) {
            setHasOverflow(false);
            return undefined;
        }
        const measure = ()=>{
            setHasOverflow(element.scrollHeight > element.clientHeight + 1);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        Array.from(element.children).forEach((child)=>observer.observe(child));
        return ()=>observer.disconnect();
    });
    return hasOverflow;
}
export function QuestionDock({ request, isSending, onAnswer }) {
    const { t } = useTranslation();
    const questions = questionsOf(request);
    const total = questions.length;
    const isBatch = total > 1;
    const isDense = questions.some((question)=>question.options.length > DENSE_OPTION_COUNT);
    const [drafts, setDrafts] = useState({});
    const [step, setStep] = useState(0);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [hasFailed, setHasFailed] = useState(false);
    const isSubmitting = useRef(false);
    const rootRef = useRef(null);
    const bodyRef = useRef(null);
    const focusTarget = useRef(null);
    const hasOverflow = useOverflow(bodyRef, !isCollapsed && !isDense);
    const isReview = isBatch && step === total;
    const question = questions[Math.min(step, total - 1)];
    const isSentOnPick = !isBatch && !question.multiSelect;
    const draftOf = (id)=>drafts[id] ?? emptyDraft();
    const left = unansweredCount(questions, drafts);
    const title = choiceTitle(request, t("chat.ask.batchTitle"));
    useLayoutEffect(()=>{
        const selector = focusTarget.current;
        const root = rootRef.current;
        focusTarget.current = null;
        if (selector && root) (root.matches(selector) ? root : root.querySelector(selector))?.focus();
    });
    const submit = async (payload)=>{
        if (isSubmitting.current) return;
        isSubmitting.current = true;
        setHasFailed(false);
        try {
            await onAnswer(request.toolCallId, payload);
        } catch  {
            setHasFailed(true);
        } finally{
            isSubmitting.current = false;
        }
    };
    const go = (target)=>{
        setStep(target);
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
        focusTarget.current = isBatch && target === total ? ".review-title" : ".question-text";
    };
    const setDraft = (id, draft)=>setDrafts((current)=>({
                ...current,
                [id]: draft
            }));
    const pick = (optionId, isPointer)=>{
        const draft = draftOf(question.id);
        const optionIds = toggledOptionIds(question, draft.optionIds, optionId);
        setDraft(question.id, {
            ...draft,
            optionIds
        });
        const isAnsweredOnPick = isSentOnPick && !isOverridden(draft);
        if (isAnsweredOnPick && isPointer && optionIds.length > 0) {
            submit({
                answers: [
                    toAnswer(question.id, {
                        optionIds,
                        customText: ""
                    })
                ]
            });
        }
    };
    const selection = selectionOf(questions, drafts);
    const canAdvance = isReview ? selection !== null : isAnswered(drafts[question.id]);
    const primaryLabel = !isBatch ? t("chat.ask.send") : isReview ? t("chat.ask.sendAnswers") : step === total - 1 ? t("chat.ask.review") : t("chat.ask.next");
    const primary = ()=>{
        if (!canAdvance || isSending) return;
        if (isBatch && !isReview) go(step + 1);
        else if (selection) submit(selection);
    };
    const collapse = (value)=>{
        setIsCollapsed(value);
        focusTarget.current = value ? ".dock-line" : ".dock-collapse";
    };
    if (isCollapsed) {
        return /*#__PURE__*/ React.createElement(CollapsedLine, {
            type: "button",
            className: "question-dock dock-line",
            "aria-expanded": false,
            title: t("chat.ask.expand"),
            onClick: ()=>collapse(false),
            ref: rootRef
        }, /*#__PURE__*/ React.createElement(QuestionIcon, {
            size: 16
        }), /*#__PURE__*/ React.createElement("b", null, t("chat.ask.waitingCount", {
            count: total
        })), /*#__PURE__*/ React.createElement("span", {
            className: "dock-title"
        }, title), /*#__PURE__*/ React.createElement("span", {
            className: "dock-chevron",
            "aria-hidden": true
        }, /*#__PURE__*/ React.createElement(ChevronUpIcon, {
            size: 16
        })));
    }
    const reach = left === 0 ? total : questions.findIndex((each)=>!isAnswered(drafts[each.id]));
    return /*#__PURE__*/ React.createElement(Card, {
        ref: rootRef,
        className: "question-dock",
        role: "region",
        "aria-label": t("chat.ask.dockLabel", {
            count: total
        }),
        "aria-busy": isSending || undefined
    }, /*#__PURE__*/ React.createElement("div", {
        className: "hook-head"
    }, /*#__PURE__*/ React.createElement(QuestionIcon, {
        size: 16
    }), /*#__PURE__*/ React.createElement("div", {
        className: "hook-text"
    }, /*#__PURE__*/ React.createElement("h4", null, title), !isBatch && request.message && /*#__PURE__*/ React.createElement("p", {
        className: "hook-desc"
    }, request.message)), /*#__PURE__*/ React.createElement(IconButton, {
        size: "sm",
        className: "dock-collapse",
        "aria-label": t("chat.ask.collapse"),
        title: t("chat.ask.collapse"),
        disabled: isSending,
        onClick: ()=>collapse(true)
    }, /*#__PURE__*/ React.createElement(ChevronDownIcon, {
        size: 16
    }))), /*#__PURE__*/ React.createElement(Body, {
        ref: bodyRef,
        "data-overflow": hasOverflow,
        "data-dense": isDense
    }, isBatch && /*#__PURE__*/ React.createElement(Progress, null, /*#__PURE__*/ React.createElement("div", {
        className: "bars"
    }, questions.map((each, index)=>{
        const isDone = isAnswered(drafts[each.id]);
        const label = {
            number: index + 1,
            question: each.header ?? each.question
        };
        return /*#__PURE__*/ React.createElement("button", {
            key: each.id,
            type: "button",
            "data-state": index === step ? "current" : isDone ? "done" : "todo",
            "aria-current": index === step ? "step" : undefined,
            "aria-label": t(isDone ? "chat.ask.stepLabelDone" : "chat.ask.stepLabel", label),
            disabled: isSending || index > reach,
            onClick: ()=>go(index)
        });
    })), /*#__PURE__*/ React.createElement("div", {
        className: "count"
    }, /*#__PURE__*/ React.createElement("span", null, isReview ? t("chat.ask.review") : t("chat.ask.counter", {
        current: step + 1,
        total
    })), /*#__PURE__*/ React.createElement("span", null, left > 0 ? t("chat.ask.unanswered", {
        count: left
    }) : t("chat.ask.allAnswered")))), isReview ? /*#__PURE__*/ React.createElement(Review, null, /*#__PURE__*/ React.createElement("p", {
        className: "review-title",
        tabIndex: -1
    }, t("chat.ask.reviewTitle")), questions.map((each, index)=>/*#__PURE__*/ React.createElement("div", {
            className: "review-row",
            key: each.id
        }, /*#__PURE__*/ React.createElement("div", {
            className: "review-body"
        }, each.header && /*#__PURE__*/ React.createElement(Tag, {
            tone: "grey"
        }, each.header), /*#__PURE__*/ React.createElement("span", null, each.question), /*#__PURE__*/ React.createElement("span", null, answerText(each, toAnswer(each.id, draftOf(each.id))))), /*#__PURE__*/ React.createElement(Button, {
            variant: "ghost",
            size: "sm",
            disabled: isSending,
            "aria-label": t("chat.ask.changeLabel", {
                question: each.header ?? each.question
            }),
            onClick: ()=>go(index)
        }, t("chat.ask.change"))))) : /*#__PURE__*/ React.createElement(QuestionSet, {
        key: question.id,
        name: `${request.toolCallId}-${question.id}`,
        question: question,
        draft: draftOf(question.id),
        isBatch: isBatch,
        isDense: question.options.length > DENSE_OPTION_COUNT,
        isDisabled: isSending,
        onPick: pick,
        onText: (customText)=>setDraft(question.id, {
                ...draftOf(question.id),
                customText
            }),
        onEnter: primary
    })), hasFailed && /*#__PURE__*/ React.createElement("p", {
        className: "hook-error",
        role: "alert"
    }, t("chat.ask.sendFailed")), /*#__PURE__*/ React.createElement("div", {
        className: "hook-actions"
    }, /*#__PURE__*/ React.createElement(Button, {
        variant: "ghost",
        className: isBatch ? "lead" : undefined,
        disabled: isSending,
        onClick: ()=>submit({
                cancelled: true
            })
    }, t("chat.ask.dismiss")), isBatch && step > 0 && /*#__PURE__*/ React.createElement(Button, {
        variant: "outline",
        startIcon: /*#__PURE__*/ React.createElement(ChevronLeftIcon, {
            size: 14
        }),
        disabled: isSending,
        onClick: ()=>go(step - 1)
    }, t("chat.ask.back")), (!isSentOnPick || canAdvance || isSending) && /*#__PURE__*/ React.createElement(Button, {
        loading: isSending,
        disabled: !canAdvance,
        onClick: primary
    }, primaryLabel)));
}
